// WORK STYLE SCREENER — Scoring Engine
// Likert 1-5 scale · Reverse-scored items handled · Traffic light output
import { WORK_STYLE_ITEMS, WORK_STYLE_DIMS } from "./questionBank.js";

// Traffic light bands — per dimension score (max 40 per dim, 8 items × 5)
export const WS_TRAFFIC = {
  green:  { label:"Green",  symbol:"✅", color:"#059669", bg:"#f0fdf4", border:"#86efac", text:"Strength — meets or exceeds frontline role requirements." },
  amber:  { label:"Amber",  symbol:"⚠",  color:"#d97706", bg:"#fffbeb", border:"#fde68a", text:"Monitor — acceptable but watch for role-specific risks." },
  red:    { label:"Red",    symbol:"🚩", color:"#dc2626", bg:"#fef2f2", border:"#fecaca", text:"Risk — dimension below threshold for this role type. Probe in interview." },
};

// Thresholds as % of max per dimension
// Based on IPIP normative data and frontline role meta-analysis
export const WS_THRESHOLDS = {
  R: { green:75, amber:55 }, // Reliability — critical for attendance and task completion
  T: { green:70, amber:50 }, // Teamwork
  C: { green:75, amber:55 }, // Compliance — critical for safety-sensitive roles
  S: { green:65, amber:45 }, // Stress Tolerance
  V: { green:70, amber:50 }, // Service — higher threshold for customer-facing roles
};

// Role-specific threshold overrides
export const WS_ROLE_PROFILES = {
  warehousing: {
    label:"Warehousing & Logistics",
    weights:{ R:1.5, T:1.0, C:1.5, S:1.2, V:0.8 }, // Reliability & Compliance most critical
    minGreen:["R","C"], // Must be green in these to proceed
  },
  retail: {
    label:"Retail & Customer Service",
    weights:{ R:1.2, T:1.2, C:1.0, S:1.2, V:1.5 }, // Service most critical
    minGreen:["V","S"],
  },
  security: {
    label:"Security & Enforcement",
    weights:{ R:1.5, T:1.0, C:2.0, S:1.3, V:0.7 }, // Compliance most critical
    minGreen:["R","C"],
  },
  manufacturing: {
    label:"Manufacturing & Production",
    weights:{ R:1.5, T:1.0, C:1.5, S:1.0, V:0.5 },
    minGreen:["R"],
  },
  general: {
    label:"General Frontline",
    weights:{ R:1.2, T:1.0, C:1.2, S:1.0, V:1.0 },
    minGreen:["R"],
  },
};

export function scoreWorkStyle(answers) {
  // answers: array of 40 values (1-5) or null
  const dimScores = { R:0, T:0, C:0, S:0, V:0 };
  const dimCounts = { R:0, T:0, C:0, S:0, V:0 };

  WORK_STYLE_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    const val = item.reverse ? (6 - raw) : raw;
    dimScores[item.dim] += val;
    dimCounts[item.dim]++;
  });

  const maxPerDim = 40; // 8 items × 5 max
  const results = {};
  let overallTotal = 0, overallMax = 0;

  Object.keys(WORK_STYLE_DIMS).forEach(dim => {
    const raw = dimScores[dim];
    const count = dimCounts[dim];
    const max = count * 5;
    const pct = max > 0 ? Math.round((raw / max) * 100) : 0;
    const thresh = WS_THRESHOLDS[dim];
    const light = pct >= thresh.green ? "green" : pct >= thresh.amber ? "amber" : "red";
    results[dim] = { raw, max, pct, light, traffic: WS_TRAFFIC[light], count };
    overallTotal += raw;
    overallMax   += max;
  });

  const overallPct = overallMax > 0 ? Math.round((overallTotal / overallMax) * 100) : 0;
  const greenCount = Object.values(results).filter(r => r.light === "green").length;
  const redCount   = Object.values(results).filter(r => r.light === "red").length;

  // Overall verdict
  const overallVerdict = greenCount >= 4 && redCount === 0
    ? { label:"Proceed to Interview", color:"#059669", symbol:"✅" }
    : redCount >= 2
    ? { label:"Not Recommended",      color:"#dc2626", symbol:"🚩" }
    : { label:"Conditional — Probe",  color:"#d97706", symbol:"⚠"  };

  return {
    dimResults: results,
    overallPct,
    overallVerdict,
    greenCount,
    redCount,
    scales: Object.fromEntries(Object.entries(results).map(([k,v])=>[`ws_${k.toLowerCase()}`, v.pct])),
    perfAvg: overallPct / 20,
    potAvg:  overallPct / 20,
    perfBox: greenCount >= 4 ? 3 : redCount >= 2 ? 1 : 2,
    potBox:  greenCount >= 4 ? 3 : redCount >= 2 ? 1 : 2,
  };
}
